import { z } from 'zod'

/**
 * A tenant's slug: **one DNS label**, and the rule lives here so that both halves of the
 * system enforce the same one.
 *
 * `docs/ENVIRONMENTS.md` §8 publishes a tenant's web app at `<slug>.tasks4staff.com` and
 * `<slug>.preprod.tasks4staff.com`, and the zone's wildcard certificate coverage is **one
 * label deep**. A slug that is not a single label therefore produces a tenant whose app
 * can never be given a certificate — a failure discovered at W02's deploy time, which is
 * years after the tenant was created and after it has apps in two stores
 * (`docs/WORKPLAN.md`, the P02 note).
 *
 * So the rule is enforced three times over, and this file is the only statement of it:
 *
 * 1. in the field the slug is typed in (`apps/platform`, so the owner learns it at the
 *    moment they can act on it),
 * 2. on the server, because a client-side rule is a hint until the server agrees
 *    (`docs/ux/PLATFORM-ADMIN.md` §9.2),
 * 3. and — for uniqueness, which no schema can check — by `tenants_slug_unq`, which is
 *    the one unique index in the schema a soft delete does not release.
 *
 * `packages/domain` imports nothing, so this is reachable from `apps/api` and
 * `apps/platform` alike and neither can drift from the other.
 */

/** The longest a single DNS label may be. */
export const MAX_TENANT_SLUG_LENGTH = 63

/**
 * Labels the platform has already spent on its own surfaces.
 *
 * `docs/ENVIRONMENTS.md` §8's table, as a list a validator can hold. A tenant holding one of
 * these would have its app published at an address that already resolves to something else,
 * and the deploy that discovered it would overwrite a DNS record without prompting
 * (`docs/WORKPLAN.md`, W02's note).
 *
 * - `api` and `admin` — the API and this console, in both environments.
 * - `www` — the zone's conventional front door.
 * - `mcp` — the remote MCP server (M01), in both environments.
 * - `preprod` — **the pre-prod environment's own label.** Prod is the bare form
 *   (`<slug>.tasks4staff.com`) and pre-prod is one label deeper
 *   (`<slug>.preprod.tasks4staff.com`), so a *production* tenant called `preprod` would take
 *   `preprod.tasks4staff.com`: the parent of every pre-prod hostname. That is the one
 *   reservation that is about the shape of the scheme rather than about a surface, and it
 *   is what keeps the two environments disjoint subtrees.
 *
 * **It is a list of names and not a pattern**, which is what lets §9.1's field show it in
 * full. An interim scheme during the rename to Tasks4Staff folded the environment into the
 * surface label and would have made *any* slug ending `-preprod` a collision — a rule
 * somebody has to keep enforcing rather than a word somebody can read. That scheme was
 * replaced before anything shipped (`docs/ux/PLATFORM-ADMIN.md` §9, the amendment).
 *
 * Shown to the owner **in full** rather than hinted at (`docs/ux/PLATFORM-ADMIN.md` §9.1),
 * which is why it is exported.
 */
export const RESERVED_TENANT_SLUGS = ['api', 'admin', 'www', 'preprod', 'mcp'] as const

/** Every way a slug can be wrong, as a closed set the client and the server both name. */
export type TenantSlugProblem =
  | 'blank'
  | 'too_long'
  | 'dotted'
  | 'charset'
  | 'hyphen_edge'
  | 'reserved'

/**
 * Why this slug is not a usable label, or `undefined` when it is.
 *
 * The order is the order the messages are most useful in: emptiness first, then the two
 * shape rules a typist hits, then the character set, then the reservation. `dotted` is
 * separated out of `charset` deliberately — a dot is the single likeliest thing an owner
 * tries and the one whose *reason* they need (`docs/ux/PLATFORM-ADMIN.md` §9.1), and a
 * generic "letters, digits and hyphens" would bury it.
 *
 * This is a pure function of its argument: it does not lowercase, does not trim, and does
 * not know whether the slug is already taken. Normalising is
 * {@link normaliseTenantSlug}'s job and uniqueness is the database's.
 */
export function tenantSlugProblem(slug: string): TenantSlugProblem | undefined {
  if (slug.length === 0) {
    return 'blank'
  }
  if (slug.includes('.')) {
    return 'dotted'
  }
  if (slug.length > MAX_TENANT_SLUG_LENGTH) {
    return 'too_long'
  }
  if (!/^[a-z0-9-]+$/.test(slug)) {
    return 'charset'
  }
  if (slug.startsWith('-') || slug.endsWith('-')) {
    return 'hyphen_edge'
  }
  if ((RESERVED_TENANT_SLUGS as readonly string[]).includes(slug)) {
    return 'reserved'
  }
  return undefined
}

/**
 * The slug as it would be stored: trimmed and lower-cased.
 *
 * A hostname is case-insensitive, so refusing a capital would be pedantry — the field
 * corrects it in place as it is typed (`docs/ux/PLATFORM-ADMIN.md` §9.1) and the server
 * applies the same correction rather than trusting that it happened. Nothing else is
 * normalised: a dot is not silently turned into a hyphen, because a slug the owner did not
 * type is a hostname they will not recognise.
 */
export function normaliseTenantSlug(slug: string): string {
  return slug.trim().toLowerCase()
}

/**
 * A developer-facing sentence per problem.
 *
 * This is the API's `message`, not the console's copy: `apps/platform` writes its own
 * sentences and never renders the API's prose (`docs/ux/PLATFORM-ADMIN.md` §20.1). What
 * both share is the rule, which is the code above.
 */
export const TENANT_SLUG_PROBLEM_MESSAGES: Readonly<Record<TenantSlugProblem, string>> = {
  blank: 'A slug is required.',
  dotted: 'A slug is one DNS label: a dot would make it two, and the certificate covers one.',
  too_long: `A slug is at most ${MAX_TENANT_SLUG_LENGTH} characters.`,
  charset: 'A slug uses lowercase letters, digits and hyphens only.',
  hyphen_edge: 'A slug cannot start or end with a hyphen.',
  reserved: `The platform’s own addresses are reserved: ${RESERVED_TENANT_SLUGS.join(', ')}.`,
}

/**
 * The slug as a schema, normalising before it validates.
 *
 * Normalising inside the schema rather than beside it means every caller gets it: the
 * route parses the body once and the value that reaches the service is the value that
 * reaches the database, so no path can store `Nord-Reinigung` and then fail to find it.
 */
export const tenantSlugSchema = z
  .string()
  .transform(normaliseTenantSlug)
  .superRefine((slug, ctx) => {
    const problem = tenantSlugProblem(slug)
    if (problem !== undefined) {
      ctx.addIssue({ code: 'custom', message: TENANT_SLUG_PROBLEM_MESSAGES[problem] })
    }
  })
